import Axios, { AxiosRequestConfig } from "axios";
import qs from 'qs';
import { ISavedRingConfiguration, IMetalGroup } from "../sharedInterfaces";
import { IMetalZone } from "../Model/RingModel";
import { ICaptureConfig } from "../Tools/Capture/CaptureConfig";

export interface ICheckoutArgument {
  SKU:string,
  fingerSize:string,
  width:number,
  thickness:number,
  metalGroups:IMetalGroup[],
  metalZones:IMetalZone[],
  stoneGroups:{
    name:string,
    stone:'White Diamond'|'Black Diamond'|'Sapphire'|'Ruby',
    stoneSize:number
  }[],
  engravingText:string,
  engravingFont:'Brush Script'|'Century Gothic'|'Snell Roundhand'|'Trajan'|'Dakota',
  thumbnail:string,
  additionalData:any
}

export class RequestService{
  private _backendURI:string;
  private _osURI:string;
  private _priceCheckURI:string;
  private _costPriceCheckURI:string;
  private _shortLinkURI:string;

  constructor() {
    this._shortLinkURI = 'https://threed.link';
  }// /constructor()

  public setOsURI = (osURI:string) => {
    this._osURI = osURI;
  }

  public setBackendURI = (beURI:string) => {
    this._backendURI = beURI;
  }

  public setPriceCheckURI = (pcURI:string) => {
    this._priceCheckURI = pcURI;
  }

  public setCostPriceCheckURI = (cpcURI:string) => {
    this._costPriceCheckURI = cpcURI;
  }

  public saveConfiguration = (configuration:ISavedRingConfiguration|ICaptureConfig):Promise<string> => {
    try {
      return Axios.post(`${this._shortLinkURI}/save-design`, {
        design: configuration
      })
      .then((res) => {
        // console.log("saveConfiguration()", res.data);
        return res.data;
      });
    } catch(err) {
      console.error('Error saving configuration!', err);
    };
  }

  public loadConfiguration = (id:string):Promise<ISavedRingConfiguration|ICaptureConfig> => {
    try {
      return Axios.get(`${this._shortLinkURI}/load-design?id=${id}`)
      .then((res) => {
        return res.data;
      });
    } catch(err) {
      console.error('Error loading configuration!', err);
    };
  }

  public getShortLink = (url:string):Promise<string> => {
    try {
      return Axios.post(`${this._shortLinkURI}/short-url`, {
        url: url
      })
      .then((res) => {
        return res.data.url;
      })
    } catch(err) {
      console.error('Error getting shortLink!', err);
    };
  }

  public fetchRingData = async (SKU?:Array<string>):Promise<any[]> => {
    const config = { params: null };
    if (SKU) config.params = { SKU: SKU }

    try {
      const res = await Axios.get(this._backendURI + '/product/list', config);
      return res.data;
    } catch(err) {
      throw(err);
    };
  }

  public generatePriceCheckConfig = (ring:ICheckoutArgument):AxiosRequestConfig => {
    const config = {
      params: {
        SKU: ring.SKU,
        size: ring.fingerSize,
        fingerSize: ring.fingerSize,
        width: ring.width,
        thickness: ring.thickness,
        metalGroups: '[]', //We probably don't actually need groups here, but right now the endpoint only works if we provide them as an argument
        metalZones: ring.metalZones,
        stoneGroups: ring.stoneGroups,
        additionalData: ring.additionalData
      },
      paramsSerializer: (params) => {
        const queryString = qs.stringify(params, { arrayFormat: 'indices', encode: false });
        return queryString;
      }
    }
    return config;      
  };

  public getPrice = async (ring:ICheckoutArgument):Promise<number> => {
    const config = this.generatePriceCheckConfig(ring);

    try {
      const res = await Axios.get(this._priceCheckURI, config)
      return Promise.resolve(res.data.price);
    } catch (err) {
      console.error("Error getting price!", err);
      console.warn("For testing/ demonstration purposes we use a random sample price.");
      return Promise.resolve(Math.floor(900 + Math.random() * 1500));
    }
  }

  public getCostPrice = (ring:ICheckoutArgument):Promise<number> => {
    const config = this.generatePriceCheckConfig(ring);

    return Axios.get(this._costPriceCheckURI, config)
    .then((res) => {
      return res.data.price;
    })
    .catch((err) => {
      //Fail silently
      return null;
    });
  };

  public getCheckoutURI = (ring:ICheckoutArgument):Promise<string> => {
    return Axios.post(`${this._osURI}/v3/api/configurator/checkout`, {
      params: {
        SKU: ring.SKU,
        size: ring.fingerSize,
        fingerSize: ring.fingerSize,
        width: ring.width,
        thickness: ring.thickness,
        metalGroups: ring.metalGroups,
        metalZones: ring.metalZones,
        stoneGroups: ring.stoneGroups,
        engravingText: ring.engravingText,
        engravingFont: ring.engravingFont,
        thumbnail: ring.thumbnail,
        additionalData: ring.additionalData
      }
    })
    .then((res) => {
      return res.data.url;
    })
    .catch((err) => {
      throw(err);
      //{"url":"http:\/\/qa.infinityorders.com.au\/viscircle\/4"}
    });
  };

}// /class